import {
  useEffect, useMemo, useRef, useState,
  type CSSProperties, type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent, type ReactNode,
} from 'react';
import { X } from 'lucide-react';

/**
 * The Allocated / Not allocated coverage panel, shared by Software Allocation
 * and Hardware Allocation.
 *
 * ⛔ EXTRACTED FROM SoftwareAllocation ON 01/10/2026, NOT REWRITTEN. Hardware
 * Allocation was built as "the hardware twin of Software Allocation" and then
 * grew its own plainer right-hand panel: no search, no filter, and the whole
 * page width to span — so on a wide screen the Allocate button sat about
 * 1,300px from the department it belonged to. Two shapes for one idea.
 *
 * ⚠ The width is what fixes the distance. The panel is a fixed, resizable
 * column (`ResizablePalette`), so the button is always next to the name
 * whatever the window is doing. Capping the row inside a full-width panel would
 * have looked the same at one size and wrong at every other.
 *
 * ⛔ GENERIC OVER THE ALLOCATION ROW. Software allocations carry Mandatory /
 * Optional; hardware allocations carry nothing. That difference is the only one
 * — so it is a render prop, and nothing else about the panel knows which tenant
 * feature it is serving.
 */

export type CoverageFilter = 'all' | 'allocated' | 'blocked' | 'unallocated';

export interface CoverageItem<TAlloc> {
  key: string;
  /** Primary text — a department code, a software title, a device name. */
  code: string;
  /** Secondary text beside it. */
  label: string;
  /** Render `code` monospace. True for department codes. */
  mono: boolean;
  /** Show the "Linked" badge (mirror groups in the By-department software view). */
  linked?: boolean;
  /** Anything else to show after the label — e.g. "no such department". */
  badge?: ReactNode;
  /** Lowercased haystack for the panel's own search. */
  search: string;
  /** The allocation row when allocated, null when not. */
  alloc: TAlloc | null;

  /**
   * It cannot be allocated because the other side already has something that
   * rules it out — the text names what (090).
   *
   * ⛔ WHY A THIRD BUCKET AND NOT A DISABLED BUTTON. "Not allocated" with an
   * Allocate button says "you may do this", and the server refuses it. A role
   * holding the High Spec laptop appeared in Laptop - Standard's "Not
   * allocated (60)" with a button that could only fail — reported 07/10/2026.
   *
   * ⛔ THE CALLER OWNS THE RULE, AND MUST LEAVE THIS UNDEFINED WHEN
   * {@link alloc} IS SET. A row cannot be both allocated and blocked, and the
   * panel does not second-guess it.
   *
   * ⚠ IT USED TO. The panel also tested `!alloc`, in two expressions, while the
   * caller tested the same thing a third way — so a negative control could
   * remove any ONE of the three and nothing failed. Three guards that cover
   * each other are three guards no test can pin. Reduced to one, here, where
   * the rule lives.
   *
   * ⚠ Undefined everywhere in Software Allocation, which shares this panel and
   * has no such rule.
   */
  heldElsewhere?: string;
  /** What `onAllocate` should allocate — the id on the other side of the pair. */
  targetId: number;
}

export function CoverageHint({ children }: { children: ReactNode }) {
  return <div className="rounded border border-dashed border-border bg-bg/40 p-4 text-sm text-darkGrey/60">{children}</div>;
}

export function LinkedBadge() {
  return <span className="shrink-0 rounded-full bg-lightBlue px-1.5 py-0.5 text-[10px] font-semibold text-navy" title="Linked to other pools — allocations mirror">Linked</span>;
}

export function CoveragePanel<TAlloc>({
  title,
  searchPlaceholder,
  items,
  admin,
  busy = false,
  note,
  renderAllocatedExtra,
  onAllocate,
  onRemove,
}: {
  title: ReactNode;
  searchPlaceholder: string;
  items: CoverageItem<TAlloc>[];
  admin: boolean;
  /** Disable both actions while a mutation is in flight. */
  busy?: boolean;
  /** Said above the lists — e.g. "no allocations, so everybody can request it". */
  note?: ReactNode;
  /** Extra control on an allocated row. Software uses it for Mandatory / Optional. */
  renderAllocatedExtra?: (alloc: TAlloc) => ReactNode;
  onAllocate: (it: CoverageItem<TAlloc>) => void;
  onRemove: (alloc: TAlloc) => void;
}) {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<CoverageFilter>('all');
  const term = q.trim().toLowerCase();

  const allocated = useMemo(
    () => items.filter((it) => it.alloc && (!term || it.search.includes(term))), [items, term]);

  // ⛔ BLOCKED IS NOT "NOT ALLOCATED". It is "cannot be, and here is what is in
  // the way" — a different answer, and the one the Allocate button was wrongly
  // offering on.
  const blocked = useMemo(
    () => items.filter((it) => it.heldElsewhere && (!term || it.search.includes(term))),
    [items, term]);

  const unallocated = useMemo(
    () => items.filter((it) => !it.alloc && !it.heldElsewhere && (!term || it.search.includes(term))),
    [items, term]);

  const allocatedTotal = items.filter((it) => it.alloc).length;
  const blockedTotal = items.filter((it) => it.heldElsewhere).length;
  const unallocatedTotal = items.length - allocatedTotal - blockedTotal;

  const chips: [CoverageFilter, string][] = [
    ['all', `All (${items.length})`],
    ['allocated', `Allocated (${allocatedTotal})`],
    // ⚠ Only when there are any. Software Allocation has no such rule and must
    // not grow an empty chip.
    ...(blockedTotal > 0
      ? ([['blocked', `Has another (${blockedTotal})`]] as [CoverageFilter, string][])
      : []),
    ['unallocated', `Not allocated (${unallocatedTotal})`],
  ];

  return (
    <div className="rounded border border-border bg-bg/40 p-3">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-darkGrey/70">{title}</h2>

      {note && <div className="mt-2 text-sm text-darkGrey/80">{note}</div>}

      <div className="mt-2 inline-flex flex-wrap rounded border border-border bg-white p-0.5 text-xs" role="tablist" aria-label="Coverage filter">
        {chips.map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={filter === k}
            onClick={() => setFilter(k)}
            className={filter === k ? 'rounded bg-navy px-2 py-1 font-medium text-white' : 'rounded px-2 py-1 font-medium text-darkGrey/70 hover:text-navy'}
          >
            {label}
          </button>
        ))}
      </div>
      <input
        aria-label={searchPlaceholder}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={searchPlaceholder}
        className="mt-2 w-full rounded border border-border bg-white px-2 py-1.5 text-sm"
      />

      <div className="mt-2 max-h-[60vh] space-y-3 overflow-y-auto">
        {filter !== 'unallocated' && filter !== 'blocked' && (
          <section aria-label="Allocated">
            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-darkGrey/50">Allocated ({allocated.length})</h3>
            <ul className="space-y-1.5">
              {allocated.map((it) => (
                <li key={it.key} className="flex items-center gap-2 rounded border border-border bg-white px-2 py-1 text-sm">
                  <CoverageLabel item={it} />
                  {renderAllocatedExtra?.(it.alloc!)}
                  {admin && (
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={`Remove ${it.code}`}
                      onClick={() => onRemove(it.alloc!)}
                      className="text-darkGrey/40 hover:text-red disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  )}
                </li>
              ))}
              {allocated.length === 0 && <li className="text-xs text-darkGrey/50">{term ? 'No allocated items match.' : 'Nothing allocated yet.'}</li>}
            </ul>
          </section>
        )}
        {/* ⛔ BETWEEN the two, because that is where it belongs in the reading:
            these are neither allocated nor available. */}
        {blockedTotal > 0 && filter !== 'allocated' && filter !== 'unallocated' && (
          <section aria-label="Has another" className="mt-3">
            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-darkGrey/50">
              Already has one from this group ({blocked.length})
            </h3>
            <ul className="space-y-1.5">
              {blocked.map((it) => (
                <li key={it.key} className="flex items-center gap-2 rounded border border-border bg-bg px-2 py-1 text-sm">
                  <CoverageLabel item={it} />
                  {/* ⚠ NAMED, not merely refused. "Cannot allocate" with no
                      reason sends somebody hunting; the name is the answer. */}
                  <span className="shrink-0 text-xs text-darkGrey/70">
                    has {it.heldElsewhere}
                  </span>
                </li>
              ))}
              {blocked.length === 0 && (
                <li className="text-xs text-darkGrey/50">No matching rows.</li>
              )}
            </ul>
          </section>
        )}

        {filter !== 'allocated' && filter !== 'blocked' && (
          <section aria-label="Not allocated">
            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-darkGrey/50">Not allocated ({unallocated.length})</h3>
            <ul className="space-y-1.5">
              {unallocated.map((it) => (
                <li key={it.key} className="flex items-center gap-2 rounded border border-border bg-white px-2 py-1 text-sm">
                  <CoverageLabel item={it} />
                  {admin && (
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={`Allocate ${it.code}`}
                      onClick={() => onAllocate(it)}
                      className="shrink-0 rounded border border-navy/30 bg-white px-2 py-0.5 text-xs font-medium text-navy hover:bg-bg disabled:opacity-50"
                    >
                      Allocate
                    </button>
                  )}
                </li>
              ))}
              {unallocated.length === 0 && <li className="text-xs text-darkGrey/50">{term ? 'No unallocated items match.' : 'Everything is allocated.'}</li>}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

function CoverageLabel<TAlloc>({ item }: { item: CoverageItem<TAlloc> }) {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-1">
      <span className={item.mono ? 'font-mono text-xs font-semibold text-navy' : 'text-sm font-semibold text-navy'}>{item.code}</span>
      {item.linked && <LinkedBadge />}
      {item.badge}
      <span className="truncate text-darkGrey/60">{item.label}</span>
    </span>
  );
}

/** A selectable row for the left column of either view. */
export function SelectRow({
  primary, secondary, mono = false, badge, count, countTone, selected, onSelect,
}: {
  primary: string;
  secondary?: string;
  mono?: boolean;
  badge?: ReactNode;
  /** The pill on the right — a number, or a word like "everyone". */
  count: ReactNode;
  countTone?: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={`flex w-full items-center gap-2 rounded border bg-white px-3 py-2 text-left shadow-sm transition-colors ${selected ? 'border-orange ring-2 ring-orange/40' : 'border-border hover:bg-bg/50'}`}
      >
        <span className={mono ? 'font-mono text-sm font-semibold text-navy' : 'text-sm font-semibold text-navy'}>{primary}</span>
        {badge === 'Linked' ? <LinkedBadge /> : badge}
        {secondary && <span className="truncate text-xs text-darkGrey/60">{secondary}</span>}
        <span className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${countTone ?? 'bg-bg text-darkGrey/70'}`}>{count}</span>
      </button>
    </li>
  );
}

// ── The resizable right-hand column ─────────────────────────────────────────
//
// ⚠ Remembered per browser. The previous fixed width was lg:w-72 (288px); the
// handle lets an admin widen it so long codes / titles are not truncated.
// Clamped so it cannot be dragged away.

const PALETTE_WIDTH_KEY = 'oscar.allocPaletteWidth';
const PALETTE_MIN = 240;
const PALETTE_MAX = 640;
const PALETTE_DEFAULT = 288;
const PALETTE_STEP = 24;

function readPaletteWidth(): number {
  try {
    const raw = Number(localStorage.getItem(PALETTE_WIDTH_KEY));
    if (Number.isFinite(raw) && raw >= PALETTE_MIN && raw <= PALETTE_MAX) return raw;
  } catch { /* private window, blocked storage — the default is fine */ }
  return PALETTE_DEFAULT;
}

export function ResizablePalette({ children }: { children: ReactNode }) {
  const [width, setWidth] = useState(readPaletteWidth);
  const widthRef = useRef(width);
  widthRef.current = width;
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);

  const persist = (w: number) => {
    try { localStorage.setItem(PALETTE_WIDTH_KEY, String(Math.round(w))); } catch { /* ignore */ }
  };
  const clamp = (w: number) => Math.min(PALETTE_MAX, Math.max(PALETTE_MIN, w));

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      setWidth(clamp(d.startWidth + (d.startX - e.clientX)));
    };
    const onUp = () => {
      if (!drag.current) return;
      drag.current = null;
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
      persist(widthRef.current);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, []);

  const onPointerDown = (e: ReactPointerEvent) => {
    drag.current = { startX: e.clientX, startWidth: widthRef.current };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  };

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const next = clamp(widthRef.current + (e.key === 'ArrowLeft' ? PALETTE_STEP : -PALETTE_STEP));
    setWidth(next);
    persist(next);
    e.preventDefault();
  };

  return (
    <aside
      className="relative w-full shrink-0 lg:sticky lg:top-4 lg:w-[var(--palette-w)]"
      style={{ ['--palette-w' as string]: `${width}px` } as CSSProperties}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panel"
        aria-valuenow={Math.round(width)}
        aria-valuemin={PALETTE_MIN}
        aria-valuemax={PALETTE_MAX}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        className="absolute -left-2.5 top-0 z-10 hidden h-full w-5 cursor-col-resize touch-none place-items-center lg:grid focus:outline-none focus-visible:ring-2 focus-visible:ring-navy"
      >
        <span className="h-10 w-1 rounded-full bg-border transition-colors hover:bg-navy/40" aria-hidden />
      </div>
      {children}
    </aside>
  );
}
